/**
 * GoogleAccounts — the labeled per-user account chips on the Google
 * connector row (docs/MULTI-ACCOUNT-GOOGLE-AND-CALENDAR-FLOW.md §2;
 * boundary decision #20). One person, several Google identities: each row
 * here is a labeled account ("personal", "family") with a ring DECLARED at
 * connect time. Secrets never reach this component — the backend returns
 * metadata only, and Connect is a whole-window OAuth redirect through the
 * state-verified callback, exactly like the household Connect above it.
 */

import React from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Loader2, PlayCircle, Plus, AlertTriangle, CalendarDays } from 'lucide-react';
import apiClient from '../../lib/apiClient';
import { confirmDialog } from '../ConfirmDialog';

const RING_LABELS = { personal: 'personal', business: 'business' };
const STATUS_TONE = { connected: 'ok', expired: 'bad', disabled: 'off' };

export default function GoogleAccounts() {
  const queryClient = useQueryClient();
  const [formOpen, setFormOpen] = React.useState(false);
  const [label, setLabel] = React.useState('');
  const [ring, setRing] = React.useState('personal');
  const [testResult, setTestResult] = React.useState(null); // { label, ok, detail }

  const { data, isLoading, error } = useQuery({
    queryKey: ['google-accounts'],
    queryFn: () => apiClient.get('/api/connectors/google/accounts'),
  });

  const connect = useMutation({
    mutationFn: ({ label: l, ring: r }) =>
      apiClient.get(`/api/connectors/google/accounts/start?label=${encodeURIComponent(l)}&ring=${encodeURIComponent(r)}`),
    onSuccess: (r) => {
      if (r?.url) window.location.href = r.url;
    },
  });

  const test = useMutation({
    mutationFn: (l) => apiClient.post(`/api/connectors/google/accounts/${encodeURIComponent(l)}/test`, {}),
    onSuccess: (r, l) => setTestResult({ label: l, ok: r.ok, detail: r.detail || r.error }),
    onError: (err, l) => setTestResult({ label: l, ok: false, detail: err.message }),
    onSettled: () => queryClient.invalidateQueries({ queryKey: ['google-accounts'] }),
  });

  const disconnect = useMutation({
    mutationFn: (l) => apiClient.delete(`/api/connectors/google/accounts/${encodeURIComponent(l)}`),
    onSettled: () => queryClient.invalidateQueries({ queryKey: ['google-accounts'] }),
  });

  const handleDisconnect = async (account) => {
    const ok = await confirmDialog({
      title: 'Disconnect account',
      message: `Disconnect the "${account.label}" Google account (${account.external_identity || 'unknown identity'})? Its stored grant is deleted; calendar tools lose this account until you reconnect.`,
      confirmLabel: 'Disconnect',
    });
    if (ok) disconnect.mutate(account.label);
  };

  const submit = () => {
    const l = label.trim().toLowerCase();
    if (!/^[a-z0-9_]{1,32}$/.test(l) || connect.isPending) return;
    connect.mutate({ label: l, ring });
  };

  const accounts = data?.accounts || [];
  const labelValid = /^[a-z0-9_]{0,32}$/.test(label);

  return (
    <div className="cxga">
      <div className="cxga-title">
        <CalendarDays size={12} aria-hidden="true" />
        Accounts
        <span className="cxga-title-note">
          one per Google identity — each declares its ring at connect time; assistants
          route by the label
        </span>
      </div>

      {isLoading && (
        <div className="cxga-loading"><Loader2 size={12} className="cxr-spin" /> Loading accounts…</div>
      )}
      {error && (
        <div className="cxr-error"><AlertTriangle size={12} aria-hidden="true" /> {error.message}</div>
      )}

      {accounts.map((a) => (
        <div key={a.label} className="cxga-row">
          <span className="cxga-label">{a.label}</span>
          <span className={`cxr-ringchip ${a.ring === 'business' ? 'cxga-ringchip--business' : ''}`}>
            {RING_LABELS[a.ring] || a.ring}
          </span>
          <span className="cxga-identity">{a.external_identity || '—'}</span>
          <span className={`cxga-status cxga-status--${STATUS_TONE[a.status] || 'off'}`}>
            {a.status === 'expired' ? 'expired — reconnect' : a.status}
          </span>
          <span className="cxga-actions">
            <button
              type="button"
              className="cxr-btn"
              disabled={test.isPending && test.variables === a.label}
              onClick={() => test.mutate(a.label)}
              title="Prove the refresh grant with a live token mint"
            >
              {test.isPending && test.variables === a.label
                ? <Loader2 size={12} className="cxr-spin" />
                : <PlayCircle size={12} aria-hidden="true" />}
              Test
            </button>
            {a.status === 'expired' && (
              <button
                type="button"
                className="cxr-btn cxr-btn--primary"
                onClick={() => connect.mutate({ label: a.label, ring: a.ring })}
              >
                Reconnect
              </button>
            )}
            <button
              type="button"
              className="cxr-btn cxr-btn--danger"
              disabled={disconnect.isPending && disconnect.variables === a.label}
              onClick={() => handleDisconnect(a)}
            >
              {disconnect.isPending && disconnect.variables === a.label
                ? <Loader2 size={12} className="cxr-spin" />
                : 'Disconnect'}
            </button>
          </span>
          {testResult?.label === a.label && (
            <span className={`cxga-test cxga-test--${testResult.ok ? 'ok' : 'bad'}`}>
              {testResult.detail}
            </span>
          )}
        </div>
      ))}

      {formOpen ? (
        <div className="cxga-form">
          <input
            className="cxr-key-input cxga-input"
            placeholder="label — e.g. personal, family"
            value={label}
            onChange={(e) => setLabel(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') submit();
              if (e.key === 'Escape') { e.stopPropagation(); setFormOpen(false); setLabel(''); }
            }}
            autoFocus
          />
          <select
            className="cxga-select"
            value={ring}
            onChange={(e) => setRing(e.target.value)}
            aria-label="ring"
          >
            <option value="personal">personal ring</option>
            <option value="business">business ring</option>
          </select>
          <button
            type="button"
            className="cxr-btn cxr-btn--primary"
            disabled={!label.trim() || !labelValid || connect.isPending}
            onClick={submit}
          >
            {connect.isPending ? <Loader2 size={12} className="cxr-spin" /> : 'Connect account'}
          </button>
          {!labelValid && <span className="cxr-key-error">a-z, 0-9, _ only</span>}
          {connect.isError && <span className="cxr-key-error">{connect.error?.message}</span>}
        </div>
      ) : (
        <button type="button" className="cxr-btn cxga-add" onClick={() => setFormOpen(true)}>
          <Plus size={12} aria-hidden="true" /> Add account
        </button>
      )}
    </div>
  );
}
